import { isApiError } from "./errors";

/** The three requests of every presigned upload: ask for a URL, PUT the bytes, tell the API it is done. */
export type UploadStage = "presign" | "put" | "finalize";

/** Failure codes of the PUT stage (presign / finalize failures keep the API's own error code). */
export const STORAGE_ERROR_CODES = Object.freeze({
  unreachable: "storage_unreachable",
  rejected: "storage_rejected",
  failed: "storage_error",
  aborted: "aborted",
  unknown: "upload_failed",
} as const);

const STORAGE_MESSAGES: Readonly<Record<string, string>> = {
  [STORAGE_ERROR_CODES.unreachable]: "We couldn't reach file storage. Check your connection and try again.",
  [STORAGE_ERROR_CODES.rejected]: "File storage refused the upload. Try again.",
  [STORAGE_ERROR_CODES.failed]: "File storage had a problem. Try again in a moment.",
  [STORAGE_ERROR_CODES.aborted]: "The upload was cancelled.",
  [STORAGE_ERROR_CODES.unknown]: "The upload didn't finish. Try again.",
};

const HTTP_SERVER_ERROR = 500;
const PERCENT = 100;
const SUCCESS_MIN = 200;
const SUCCESS_MAX = 300;
/** Request headers a script may not set (the browser derives them from the body and URL). */
const FORBIDDEN_HEADERS: ReadonlySet<string> = new Set(["content-length", "host"]);

export interface UploadErrorInit {
  stage: UploadStage;
  /** HTTP status of the failing request; `0` when the browser got no response (offline, CORS refusal, abort). */
  status: number;
  code: string;
  message: string;
  requestId?: string | undefined;
  retryable: boolean;
  cause?: unknown;
}

/**
 * One shape for every upload failure: which request failed (`stage`), with what `status` and `code`.
 * `message` is user-safe (the API's own text, or a generic storage line) - never a response body.
 * `reference` is a short, PII-free token for support to quote and to find in the API logs.
 */
export class UploadError extends Error {
  readonly stage: UploadStage;
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly retryable: boolean;

  constructor(init: UploadErrorInit) {
    super(init.message, { cause: init.cause });
    this.name = "UploadError";
    this.stage = init.stage;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.retryable = init.retryable;
  }

  /** `put/403`, `finalize/503 req_ab12` - stage, status and (when the API sent one) the request id. */
  get reference(): string {
    return `${this.stage}/${String(this.status)}${this.requestId ? ` ${this.requestId}` : ""}`;
  }

  /** True for storage-side PUT failures and server faults; false for rejected files and plan limits. */
  get isStorageFailure(): boolean {
    return this.stage === "put";
  }
}

export function isUploadError(value: unknown): value is UploadError {
  return value instanceof UploadError;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function storageFailure(status: number, cause?: unknown): UploadError {
  let code: string = STORAGE_ERROR_CODES.rejected;
  if (status === 0) code = STORAGE_ERROR_CODES.unreachable;
  else if (status >= HTTP_SERVER_ERROR) code = STORAGE_ERROR_CODES.failed;
  // A refused or expired signature is fixed by asking for a fresh URL, so every storage failure retries.
  return new UploadError({ stage: "put", status, code, message: STORAGE_MESSAGES[code] ?? "", retryable: true, cause });
}

/** Wraps whatever a stage threw (`ApiError`, abort, anything) into an {@link UploadError}. */
export function toUploadError(stage: UploadStage, error: unknown): UploadError {
  if (isUploadError(error)) return error;
  if (isApiError(error)) {
    return new UploadError({
      stage,
      status: error.status,
      code: error.code,
      message: error.message,
      requestId: error.requestId,
      retryable: error.isRetryable,
      cause: error,
    });
  }
  if (isAbort(error)) {
    return new UploadError({ stage, status: 0, code: STORAGE_ERROR_CODES.aborted, message: STORAGE_MESSAGES[STORAGE_ERROR_CODES.aborted] ?? "", retryable: false, cause: error });
  }
  const code = STORAGE_ERROR_CODES.unknown;
  return new UploadError({ stage, status: 0, code, message: STORAGE_MESSAGES[code] ?? "", retryable: false, cause: error });
}

/** Runs one request of the upload and tags any failure with its stage. */
export async function inStage<T>(stage: UploadStage, work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (error) {
    throw toUploadError(stage, error);
  }
}

/** Whether offering "Try again" makes sense for this failure (transport and server faults, not rejections). */
export function isRetryableUpload(error: unknown): boolean {
  if (isUploadError(error)) return error.retryable;
  return isApiError(error) && error.isRetryable;
}

/** A presigned PUT as the API returns it (`UploadTicket` for sources, `UploadOut` for brand assets). */
export interface PresignedPut {
  url: string;
  method?: string | null | undefined;
  headers: Readonly<Record<string, string>>;
}

export interface PutOptions {
  /** Whole-percent byte progress. */
  onProgress?: ((percent: number) => void) | undefined;
  signal?: AbortSignal | undefined;
  /** Injected for tests. */
  createXhr?: (() => XMLHttpRequest) | undefined;
}

/**
 * PUTs `body` to the presigned URL with exactly the headers the API signed (XHR: `fetch` has no
 * upload progress). Every failure rejects with an {@link UploadError} of stage `put`; status `0`
 * means the browser never saw a response - offline, or the bucket's CORS policy refused the PUT.
 */
export function putToPresignedUrl(ticket: PresignedPut, body: Blob, options: PutOptions = {}): Promise<void> {
  const { onProgress, signal } = options;
  if (signal?.aborted) return Promise.reject(toUploadError("put", new DOMException("aborted", "AbortError")));
  const xhr = options.createXhr?.() ?? new XMLHttpRequest();
  return new Promise<void>((resolve, reject) => {
    xhr.open(ticket.method ?? "PUT", ticket.url);
    for (const [name, value] of Object.entries(ticket.headers)) {
      if (!FORBIDDEN_HEADERS.has(name.toLowerCase())) xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(Math.round((event.loaded / event.total) * PERCENT));
    };
    xhr.onload = () => {
      if (xhr.status >= SUCCESS_MIN && xhr.status < SUCCESS_MAX) {
        onProgress?.(PERCENT);
        resolve();
      } else reject(storageFailure(xhr.status));
    };
    xhr.onerror = () => {
      reject(storageFailure(0));
    };
    xhr.onabort = () => {
      reject(toUploadError("put", new DOMException("aborted", "AbortError")));
    };
    signal?.addEventListener("abort", () => { xhr.abort(); }, { once: true });
    xhr.send(body);
  });
}

/** What a brand-asset presign returns: where to PUT and the public URL the saved record keeps. */
export interface AssetTicket {
  upload: PresignedPut;
  public_url: string;
}

/**
 * Presign -> PUT for the small public images (workspace logo, widget avatar). The caller saves
 * `public_url` afterwards; each failure carries its stage.
 */
export async function uploadAsset(request: () => Promise<AssetTicket>, file: Blob, options: PutOptions = {}): Promise<string> {
  const ticket = await inStage("presign", request);
  await putToPresignedUrl(ticket.upload, file, options);
  return ticket.public_url;
}
